import React, { useState, useEffect } from "react";
import "../css/accountmanagement.css";
import Navbar from "../components/navbar";
import { Helmet } from "react-helmet-async";
import Footer from "../components/footer";

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://resumify.co.uk"
    : "http://localhost:5000";

export default function AccountSettings() {
  const [userStatus, setUserStatus] = useState({
    isPro: false,
    isCanceling: false,
    cancelDate: null,
  });
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });

  // Password State
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [passMessage, setPassMessage] = useState({ type: "", text: "" });

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/user/status`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          // If the backend returns an unauthorized/forbidden status (e.g., session expired)
          if (res.status === 401 || res.status === 403) {
            localStorage.removeItem("user"); // Clear the frontend state
            window.location.href = "/auth"; // Redirect to your login page
          }
          throw new Error("Unauthorized or session expired");
        }
        return res.json();
      })
      .then((data) => {
        setUserStatus({
          isPro: data.isPro,
          isCanceling: data.isCanceling,
          cancelDate: data.cancelDate,
        });

        setUserInfo({
          name: data.name || "",
          email: data.email || "",
        });
      })
      .catch((err) => {
        console.error("Status fetch failed:", err);
      });
  }, []);

  const reqs = {
    length: passwords.new.length >= 8,
    upper: /[A-Z]/.test(passwords.new),
    lower: /[a-z]/.test(passwords.new),
    number: /[0-9]/.test(passwords.new),
    special: /[!@#$%^&*]/.test(passwords.new),
  };
  const isPasswordValid =
    Object.values(reqs).every(Boolean) && passwords.new === passwords.confirm;

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) return;

    try {
      const res = await fetch(`${API_URL}/api/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Required for session cookie
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.new,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setPassMessage({
          type: "success",
          text: "Password updated successfully!",
        });
        setPasswords({ current: "", new: "", confirm: "" });
      } else {
        setPassMessage({ type: "error", text: data.message });
      }
    } catch (err) {
      setPassMessage({ type: "error", text: "Something went wrong." });
    }
  };

  const handleSubscriptionAction = async () => {
    if (userStatus.isPro && !userStatus.isCanceling) {
      // Cancel Subscription
      const res = await fetch(`${API_URL}/api/cancel-subscription`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) window.location.reload();
    } else {
      // Upgrade
      const res = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan: "pro" }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/delete-account`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.ok) {
        // CRITICAL FIX: Destroy frontend state so React doesn't auto-login
        localStorage.removeItem("user");
        window.location.href = "/auth";
      } else {
        const errorData = await res.json();
        console.error("Delete failed:", errorData.message);
        alert(errorData.message || "Failed to delete account.");
      }
    } catch (err) {
      console.error("Network error during deletion:", err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Resumify | Account Management</title>
        <meta
          name="description"
          content="Manage your account settings, subscription, and security preferences."
        />
      </Helmet>
      <Navbar />
      <div className="dashboard-main">
        <div className="main-header">
          <div>
            <h1>Account Settings</h1>
            <p>Manage your profile, subscription, and security preferences.</p>
          </div>
        </div>

        <div className="settings-grid">
          {/* PROFILE SECTION */}
          <div className="settings-card">
            <div className="settings-header">
              <h3>Profile Information</h3>
              <p>Your personal account details.</p>
            </div>
            <div className="settings-body">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  className="settings-input"
                  value={userInfo.name}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  className="settings-input"
                  value={userInfo.email}
                  disabled
                />
              </div>
              <p className="settings-hint">
                To change your email address, please contact support.
              </p>
            </div>
          </div>

          {/* SUBSCRIPTION SECTION */}
          <div className="settings-card">
            <div className="settings-header">
              <h3>Subscription Plan</h3>
              <p>Manage your billing and plan details.</p>
            </div>
            <div className="settings-body">
              <div className="plan-status-box">
                <div className="plan-info">
                  <span className="plan-badge">
                    {userStatus.isPro ? "Pro Plan" : "Free Plan"}
                  </span>
                  <p className="plan-desc">
                    {userStatus.isPro
                      ? userStatus.isCanceling
                        ? `Your plan will cancel on ${userStatus.cancelDate}.`
                        : "You have unlimited access to all Pro features."
                      : "Upgrade to Pro for unlimited PDF downloads and AI generations."}
                  </p>
                </div>

                <button
                  className={`btn ${userStatus.isPro && !userStatus.isCanceling ? "btn-secondary" : "btn-primary"}`}
                  onClick={handleSubscriptionAction}
                >
                  {userStatus.isPro && !userStatus.isCanceling
                    ? "Cancel Subscription"
                    : "Upgrade to Pro"}
                </button>
              </div>
            </div>
          </div>

          {/* SECURITY SECTION */}
          <div className="settings-card">
            <div className="settings-header">
              <h3>Security</h3>
              <p>Update your password to keep your account secure.</p>
            </div>
            <div className="settings-body">
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    className="settings-input"
                    value={passwords.current}
                    onChange={(e) =>
                      setPasswords({ ...passwords, current: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    className="settings-input"
                    value={passwords.new}
                    onChange={(e) =>
                      setPasswords({ ...passwords, new: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="password-requirements">
                  <p>Password requirements:</p>
                  <ul>
                    <li className={reqs.length ? "met" : ""}>
                      At least 8 characters
                    </li>
                    <li className={reqs.upper ? "met" : ""}>
                      One uppercase letter
                    </li>
                    <li className={reqs.lower ? "met" : ""}>
                      One lowercase letter
                    </li>
                    <li className={reqs.number ? "met" : ""}>One number</li>
                    <li className={reqs.special ? "met" : ""}>
                      One special character (!@#$%^&*)
                    </li>
                  </ul>
                </div>

                <div className="form-group mt-4">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    className={`settings-input ${passwords.confirm && passwords.new !== passwords.confirm ? "input-error" : ""}`}
                    value={passwords.confirm}
                    onChange={(e) =>
                      setPasswords({ ...passwords, confirm: e.target.value })
                    }
                    required
                  />
                </div>

                {passMessage.text && (
                  <div className={`message-alert ${passMessage.type}`}>
                    {passMessage.text}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary mt-4"
                  disabled={!isPasswordValid}
                >
                  Update Password
                </button>
              </form>
            </div>
          </div>

          {/* DANGER ZONE */}
          <div className="settings-card danger-zone">
            <div className="settings-header">
              <h3 className="text-danger">Danger Zone</h3>
              <p>Permanently delete your account and all associated data.</p>
            </div>
            <div className="settings-body">
              <p className="danger-text">
                Once you delete your account, there is no going back. All your
                CVs, data, and active subscriptions will be immediately erased.
              </p>
              <button
                className="btn btn-danger mt-4"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* CUSTOM MODAL */}
        {showDeleteModal && (
          <div className="custom-modal-overlay">
            <div className="custom-modal-content">
              <div className="modal-icon-danger">⚠</div>
              <h3>Are you absolutely sure?</h3>
              <p>
                This action cannot be undone. This will permanently delete your
                account and wipe all your saved CVs.
              </p>
              <div className="custom-modal-actions">
                <button
                  className="modal-btn cancel"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-btn delete"
                  onClick={handleDeleteAccount}
                >
                  Yes, Delete My Account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
